import { dictToB64 } from "@terra-money/terra.js/dist/util/contract";
import msgTypes from "./msgTypes";
import { GenericAuthorization } from "cosmjs-types/cosmos/authz/v1beta1/authz";
import { StakeAuthorization } from "cosmjs-types/cosmos/staking/v1beta1/authz";
import { Timestamp } from "cosmjs-types/google/protobuf/timestamp";

const getStakeRawTx = ({
  shouldWithdrawRewards,
  rewards,
  msgTypes,
  amount,
  denom,
  feeDenom,
  delegatorAddr,
  validatorAddr,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => {
  let msgs = getStakeMsgs({
    msgType: msgTypes[0],
    amount,
    delegatorAddr,
    validatorAddr,
    denom,
  });
  if (shouldWithdrawRewards && rewards.length) {
    msgs = [
      ...getClaimRewardsMsgs({
        msgType: msgTypes[1],
        delegatorAddr,
        validators: rewards,
      }),
      ...msgs,
    ];
  }
  return {
    ...getTxMeta({
      chainId,
      denom: feeDenom || denom,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    }),
    msgs,
  };
};

const getUnstakeRawTx = getStakeRawTx;

const getClaimRewardsRawTx = ({
  msgTypes,
  shouldStake,
  amount,
  denom,
  feeDenom,
  delegatorAddr,
  validators,
  validatorAddr,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => {
  let msgs = getClaimRewardsMsgs({
    msgType: msgTypes[0],
    validators,
    delegatorAddr,
  });
  if (shouldStake) {
    msgs = [
      ...msgs,
      ...getStakeMsgs({
        msgType: msgTypes[1],
        amount,
        denom,
        delegatorAddr,
        validatorAddr,
      }),
    ];
  }
  return {
    ...getTxMeta({
      chainId,
      denom: feeDenom || denom,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    }),
    msgs,
  };
};

const getRedelegateRawTx = ({
  msgTypes,
  amount,
  denom,
  feeDenom,
  delegatorAddr,
  validatorAddrSrc,
  validatorAddrDst,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => ({
  ...getTxMeta({
    chainId,
    denom: feeDenom || denom,
    gasPrice,
    gas,
    memo,
    accountNumber,
    sequence,
  }),
  msgs: getRedelegateMsgs({
    msgType: msgTypes[0],
    amount,
    denom,
    delegatorAddr,
    validatorAddrSrc,
    validatorAddrDst,
  }),
});

const getSendRawTx = ({
  msgTypes,
  amount,
  denom,
  feeDenom,
  fromAddress,
  toAddress,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => ({
  ...getTxMeta({
    chainId,
    denom: feeDenom || denom,
    gasPrice,
    gas,
    memo,
    accountNumber,
    sequence,
  }),
  msgs: getSendMsgs({
    msgType: msgTypes[0],
    amount,
    denom,
    fromAddress,
    toAddress,
  }),
});

const getSwapRawTx = ({
  msgTypes,
  amount,
  feeDenom,
  askDenom,
  offerDenom,
  fromAddress,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => ({
  ...getTxMeta({
    chainId,
    denom: feeDenom,
    gasPrice,
    gas,
    memo,
    accountNumber,
    sequence,
  }),
  msgs: getSwapMsgs({
    msgType: msgTypes[0],
    amount,
    offerDenom,
    askDenom,
    fromAddress,
  }),
});

const getExecuteContractRawTx = ({
  msgTypes,
  sender,
  contract,
  coins,
  executeMsg,
  feeDenom,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => ({
  ...getTxMeta({
    chainId,
    denom: feeDenom,
    gasPrice,
    gas,
    memo,
    accountNumber,
    sequence,
  }),
  msgs: getExecuteContractMsgs({
    msgType: msgTypes[0],
    sender,
    contract,
    coins,
    executeMsg,
  }),
});

const getVoteRawTx = ({
  msgTypes,
  voter,
  proposalOption,
  proposalId,
  denom,
  feeDenom,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => ({
  ...getTxMeta({
    chainId,
    denom: feeDenom || denom,
    gasPrice,
    gas,
    memo,
    accountNumber,
    sequence,
  }),
  msgs: getVoteMsgs({
    msgType: msgTypes[0],
    voter,
    proposalId,
    proposalOption,
  }),
});

const getDepositRawTx = ({
  msgTypes,
  depositor,
  amount,
  proposalId,
  denom,
  feeDenom,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => ({
  ...getTxMeta({
    chainId,
    denom: feeDenom || denom,
    gasPrice,
    gas,
    memo,
    accountNumber,
    sequence,
  }),
  msgs: getDepositMsgs({
    msgType: msgTypes[0],
    proposalId,
    depositor,
    amount,
    denom,
  }),
});

const getSwapExactAmountInRawTx = ({
  msgTypes,
  sender,
  routes,
  tokenIn,
  tokenOutMinAmount,
  denom,
  feeDenom,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => ({
  ...getTxMeta({
    chainId,
    denom: feeDenom || denom,
    gasPrice,
    gas,
    memo,
    accountNumber,
    sequence,
  }),
  msgs: getSwapExactAmountInMsgs({
    msgType: msgTypes[0],
    sender,
    routes,
    tokenIn,
    tokenOutMinAmount,
  }),
});

const getEnableAutoCompoundRawTx = ({
  msgTypes,
  granter,
  grantee,
  validatorAddr,
  expiration,
  feeDenom,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => ({
  ...getTxMeta({
    chainId,
    denom: feeDenom,
    gasPrice,
    gas,
    memo,
    accountNumber,
    sequence,
  }),
  msgs: getEnableAutoCompoundInMsgs({
    msgType: msgTypes[0],
    granter,
    grantee,
    validatorAddr,
    expiration,
  }),
});

const getDisableAutoCompoundRawTx = ({
  msgTypes,
  granter,
  grantee,
  grants,
  feeDenom,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => ({
  ...getTxMeta({
    chainId,
    denom: feeDenom,
    gasPrice,
    gas,
    memo,
    accountNumber,
    sequence,
  }),
  msgs: getDisableAutoCompoundInMsgs({
    msgType: msgTypes[0],
    granter,
    grantee,
    grants,
  }),
});

const getGrantLpAutoCompoundRawTx = ({
  msgTypes,
  granter,
  grantee,
  expiration,
  feeDenom,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => ({
  ...getTxMeta({
    chainId,
    denom: feeDenom,
    gasPrice,
    gas,
    memo,
    accountNumber,
    sequence,
  }),
  msgs: getGrantLpAutoCompoundInMsgs({
    msgType: msgTypes[0],
    granter,
    grantee,
    expiration,
  }),
});

const getRevokeLpAutoCompoundRawTx = ({
  msgTypes,
  granter,
  grantee,
  feeDenom,
  chainId,
  gasPrice,
  gas,
  memo,
  accountNumber,
  sequence,
}) => ({
  ...getTxMeta({
    chainId,
    denom: feeDenom,
    gasPrice,
    gas,
    memo,
    accountNumber,
    sequence,
  }),
  msgs: getRevokeLpAutoCompoundInMsgs({
    msgType: msgTypes[0],
    granter,
    grantee,
  }),
});

// common tx info
const getTxMeta = ({
  accountNumber,
  chainId,
  gasPrice,
  gas,
  sequence,
  memo = "",
  denom,
}) => ({
  account_number: String(accountNumber),
  chain_id: chainId,
  fee: {
    amount: [{ amount: String(gasPrice), denom }],
    gas: String(gas),
  },
  memo: String(memo || ""),
  sequence: String(sequence),
});

// tx messages
const getStakeMsgs = ({
  msgType,
  amount,
  delegatorAddr,
  validatorAddr,
  denom,
}) => {
  return [
    {
      type: msgType,
      value: {
        amount: { amount: String(amount), denom },
        delegator_address: delegatorAddr,
        validator_address: validatorAddr,
      },
    },
  ];
};

const getClaimRewardsMsgs = ({ msgType, validators, delegatorAddr }) => {
  return validators.map(({ validatorAddr }) => ({
    type: msgType,
    value: {
      delegator_address: delegatorAddr,
      validator_address: validatorAddr,
    },
  }));
};

const getRedelegateMsgs = ({
  msgType,
  amount,
  denom,
  delegatorAddr,
  validatorAddrSrc,
  validatorAddrDst,
}) => [
  {
    type: msgType,
    value: {
      amount: {
        amount: String(amount),
        denom,
      },
      delegator_address: delegatorAddr,
      validator_dst_address: validatorAddrDst,
      validator_src_address: validatorAddrSrc,
    },
  },
];

const getSendMsgs = ({ msgType, amount, denom, fromAddress, toAddress }) => [
  {
    type: msgType,
    value: {
      amount: [
        {
          amount: String(amount),
          denom,
        },
      ],
      from_address: fromAddress,
      to_address: toAddress,
    },
  },
];

const getSwapMsgs = ({
  msgType,
  amount,
  askDenom,
  offerDenom,
  fromAddress,
}) => [
  {
    type: msgType,
    value: {
      trader: fromAddress,
      ask_denom: askDenom,
      offer_coin: {
        amount: String(amount),
        denom: offerDenom,
      },
    },
  },
];

const getExecuteContractMsgs = ({
  msgType,
  sender,
  contract,
  coins,
  executeMsg,
}) => [
  {
    type: msgType,
    value: {
      sender,
      contract,
      coins,
      execute_msg: dictToB64(executeMsg),
    },
  },
];

const getVoteMsgs = ({ msgType, proposalId, proposalOption, voter }) => [
  {
    type: msgType,
    value: {
      option: proposalOption,
      proposal_id: String(proposalId),
      voter,
    },
  },
];

const getDepositMsgs = ({ msgType, proposalId, amount, denom, depositer }) => [
  {
    type: msgType,
    value: {
      proposal_id: String(proposalId),
      depositer,
      amount: [
        {
          amount: String(amount),
          denom,
        },
      ],
    },
  },
];

const getSwapExactAmountInMsgs = ({
  msgType,
  tokenOutMinAmount,
  routes,
  tokenIn,
  sender,
}) => [
  {
    type: msgType,
    value: {
      sender,
      routes,
      tokenIn,
      tokenOutMinAmount,
    },
  },
];

const getEnableAutoCompoundInMsgs = ({
  msgType,
  granter,
  grantee,
  validatorAddr,
  expiration,
}) => [
  {
    type: msgType,
    value: {
      granter,
      grantee,
      grant: {
        authorization: {
          typeUrl: "/cosmos.staking.v1beta1.StakeAuthorization",
          value: StakeAuthorization.encode(
            StakeAuthorization.fromPartial({
              allowList: { address: [validatorAddr] },
              authorizationType: 1,
            })
          ).finish(),
        },
        expiration: Timestamp.fromPartial({
          seconds: expiration / 1000,
          nanos: 0,
        }),
      },
    },
  },
];

const getDisableAutoCompoundInMsgs = ({ msgType, granter, grantee, grants }) =>
  grants.map((msgTypeUrl) => ({
    type: msgType,
    value: {
      granter,
      grantee,
      msg_type_url: msgTypeUrlMap[msgTypeUrl] || msgTypeUrl,
    },
  }));

const getGrantLpAutoCompoundInMsgs = ({
  msgType,
  granter,
  grantee,
  expiration,
}) => [
  {
    type: msgType,
    value: {
      granter,
      grantee,
      grant: {
        authorization: {
          typeUrl: "/cosmos.authz.v1beta1.GenericAuthorization",
          value: GenericAuthorization.encode({
            msg: "/osmosis.gamm.v1beta1.MsgJoinSwapExternAmountIn",
          }).finish(),
        },
        expiration: Timestamp.fromPartial({
          seconds: expiration / 1000,
          nanos: 0,
        }),
      },
    },
  },
  {
    type: msgType,
    value: {
      granter,
      grantee,
      grant: {
        authorization: {
          typeUrl: "/cosmos.authz.v1beta1.GenericAuthorization",
          value: GenericAuthorization.encode({
            msg: "/osmosis.lockup.MsgLockTokens",
          }).finish(),
        },
        expiration: Timestamp.fromPartial({
          seconds: expiration / 1000,
          nanos: 0,
        }),
      },
    },
  },
];

const getRevokeLpAutoCompoundInMsgs = ({ msgType, granter, grantee }) => [
  {
    type: msgType,
    value: {
      granter,
      grantee,
      msg_type_url: "/osmosis.gamm.v1beta1.MsgJoinSwapExternAmountIn",
    },
  },
  {
    type: msgType,
    value: {
      granter,
      grantee,
      msg_type_url: "/osmosis.lockup.MsgLockTokens",
    },
  },
];

const txsMap = {
  stake: getStakeRawTx,
  unstake: getUnstakeRawTx,
  claim: getClaimRewardsRawTx,
  redelegate: getRedelegateRawTx,
  send: getSendRawTx,
  swap: getSwapRawTx,
  executeContract: getExecuteContractRawTx,
  vote: getVoteRawTx,
  deposit: getDepositRawTx,
  swapExactAmountIn: getSwapExactAmountInRawTx,
  enableAutoCompound: getEnableAutoCompoundRawTx,
  disableAutoCompound: getDisableAutoCompoundRawTx,
  grantLpAutocompound: getGrantLpAutoCompoundRawTx,
  revokeLpAutocompound: getRevokeLpAutoCompoundRawTx,
};

const msgTypeUrlMap = {
  ["/cosmos.authz.v1beta1.GenericAuthorization"]:
    "/cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward",
  ["/cosmos.staking.v1beta1.StakeAuthorization"]:
    "/cosmos.staking.v1beta1.MsgDelegate",
};

const msgTypesMap = {
  stake: ["Stake", "ClaimRewards"],
  unstake: ["Unstake"],
  claim: ["ClaimRewards", "Stake"],
  redelegate: ["Redelegate"],
  send: ["Send"],
  swap: ["Swap"],
  executeContract: ["ExecuteContract"],
  vote: ["Vote"],
  deposit: ["Deposit"],
  swapExactAmountIn: ["SwapExactAmountIn"],
  enableAutoCompound: ["EnableAutoCompound"],
  disableAutoCompound: ["DisableAutoCompound"],
  grantLpAutocompound: ["GrantLpAutocompound"],
  revokeLpAutocompound: ["RevokeLpAutocompound"],
};

const getRawTx = (project) => (txType, data) => {
  const msgTypes = getMsgTypes(txType, project);
  return txsMap[txType]({ ...data, msgTypes });
};

const getMsgTypes = (txType, project) => {
  const msgTypeNames = msgTypesMap[txType];
  return msgTypeNames.map((typeName) => project.msgTypes[typeName]);
};

const getProject = (projectName) => {
  return {
    msgTypes: msgTypes[projectName],
  };
};

export default (projectName) => {
  const project = getProject(projectName);
  return {
    getRawTx: getRawTx(project),
  };
};
